import { useCallback, useRef } from 'react';

import { useBoolean, useDidUpdate } from '@enterprise/hooks';

import type { SlideOutProps } from '#slide-out/types/slide-out.props';

/**
 * Custom hook to manage the configuration and state of a SlideOut component.
 *
 * This hook handles the open/close state, extracts control and content children,
 * and provides references and handlers for SlideOut behavior.
 *
 * @param params - Object containing SlideOut properties:
 *   - `children`: The child elements of the SlideOut.
 *   - `isOpen`: Boolean indicating if the SlideOut is open.
 *   - `onClose`: Optional callback invoked when the SlideOut is closed.
 *   - `onOpen`: Optional callback invoked when the SlideOut is opened.
 *
 * @returns An object containing:
 *   - `bodyRef`: Ref to the SlideOut body element.
 *   - `contentChildren`: Array of content child elements (excluding controls).
 *   - `controls`: The SlideOut.Controls element, if present.
 *   - `handleOnClose`: Handler to close the SlideOut and invoke `onClose`.
 *   - `open`: Boolean state indicating if the SlideOut is open.
 *   - `setClosed`: Function to set the SlideOut as closed.
 *
 * @throws Error if more than one <SlideOut.Controls> is found in development mode.
 */
export const useSlideOutConfiguration = ({
  isOpen,
  onClose,
  onOpen,
}: Pick<SlideOutProps, 'isOpen' | 'onClose' | 'onOpen'>) => {
  const [open, { off: setClosed, on: setOpen }] = useBoolean(isOpen);
  const bodyRef = useRef<HTMLDivElement>(null);

  const handleOnClose = useCallback(() => {
    onClose?.();
    setClosed();
  }, [onClose, setClosed]);

  useDidUpdate(() => {
    if (isOpen) {
      onOpen?.();
      setOpen();
    } else {
      setClosed();
    }
  }, [isOpen]);

  return {
    bodyRef,
    handleOnClose,
    open,
    setClosed,
  };
};
